// Channel rows for the INLINE device card (the "Details" expansion on the
// Devices list), as opposed to the full device record page.
//
// Same source of truth as the detail page — `deviceChannelRows` — so the two
// surfaces can never disagree about which channels a device has or what they
// are called. The only thing this module adds is the one presentation decision
// the list card needs and the detail page doesn't: a list row must stay a list
// row, so a module reporting an implausible number of channels can't push every
// other device off the screen. Real Maretron SIM100/RIM100 banks are six
// channels (core `SIM100_INDICATOR_CHANNELS` / `RIM100_INDICATOR_CHANNELS`),
// and the boat's own backstop cap is 64 — so the visible cap here sits above
// every real module and only ever engages on an anomalous map, with a note
// pointing at the full record rather than silently dropping channels.

import { deviceChannelRows, type DeviceChannelRow } from './deviceChannels';
import type { VesselDevice } from './useBoatDevices';

/**
 * Most channel rows the inline card will render. Deliberately above the six
 * channels of every wire-confirmed Maretron module, so real hardware is shown
 * in full and truncation is the anomaly path, not the normal one.
 */
export const DEVICE_CARD_CHANNEL_LIMIT = 8;

export interface DeviceCardChannels {
  /** Channels to render, in channel order. Empty means: render nothing. */
  rows: DeviceChannelRow[];
  /**
   * One-sentence note when channels were held back, else `null`. Never a bare
   * count: a truncated list must say where the rest of them are.
   */
  overflowNote: string | null;
}

/**
 * Channels for one device's inline card.
 *
 * Returns `{ rows: [], overflowNote: null }` when the device reports no
 * channels — nearly every device on a boat (a GPS or a depth sounder has no
 * concept of channels), so the caller renders nothing at all rather than an
 * empty section.
 */
export function deviceCardChannels(device: VesselDevice): DeviceCardChannels {
  const all = deviceChannelRows(device);
  const hidden = all.length - DEVICE_CARD_CHANNEL_LIMIT;
  if (hidden <= 0) return { rows: all, overflowNote: null };
  return {
    rows: all.slice(0, DEVICE_CARD_CHANNEL_LIMIT),
    overflowNote:
      hidden === 1
        ? '1 more channel is listed in the full device record below.'
        : `${hidden} more channels are listed in the full device record below.`,
  };
}
